import { useState, useEffect, useMemo } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAccount } from "../../../context/AccountContext";
import { useFetchTopSpaces } from "../../../services/spaceService";

export const SpaceSwitcher = (props) => {
  const { t } = useTranslation();
  const { account, updateAccount } = useAccount();
  const { data, isPending } = useFetchTopSpaces();

  const spaces = useMemo(() => data?.content || [], [data]);

  const [currentSpace, setCurrentSpace] = useState(null);
  const [isReloading, setIsReloading] = useState(false);
  const [tempSelectedSpace, setTempSelectedSpace] = useState(null);

  // Effect to set the initial space when account data is loaded
  useEffect(() => {
    if (!account || isPending) {
      return;
    }
    const spaceUuid = account?.space;

    if (spaceUuid && spaces.length > 0) {
      const selectedSpace = spaces.find((space) => space.uuid === spaceUuid);
      if (selectedSpace) {
        setCurrentSpace(selectedSpace);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, spaces]);

  if (isPending || !spaces) {
    return (
      <div className="placeholder-wave">
        <div className="placeholder rounded w-100 h-100"></div>
      </div>
    );
  }

  const handleSpaceSelect = (space) => {
    if (currentSpace?.uuid === space.uuid) return;

    setIsReloading(true);
    setTempSelectedSpace(space.uuid);

    updateAccount({
      space: space.uuid,
    });
    window.location.reload();
  };

  const filteredSpaces =
    spaces.length > 0
      ? spaces?.filter(
          (space) => !currentSpace || space.uuid !== currentSpace.uuid
        )
      : [];

  return (
    <Dropdown className="space-switcher-dropdown-toggle w-100" {...props}>
      <Dropdown.Toggle
        variant="none"
        className="p-0 border-0 w-100 d-block"
        id="space-switcher"
        aria-label={t("spaces.dropdown.menu")}
        aria-expanded={false}
        aria-haspopup="true"
        aria-live="polite"
        aria-atomic="true"
      >
        <div className="card border-0 shadow-none py-2 px-3 bg-light">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center text-truncate gap-2">
              <i className="bi bi-grid text-secondary d-flex align-items-center"></i>
              <div className="mb-0 text-truncate">
                {currentSpace?.name ? (
                  currentSpace.name === "Personal" ? (
                    t("spaces.personal")
                  ) : (
                    currentSpace.name
                  )
                ) : (
                  <div
                    className="placeholder-wave"
                    style={{ width: "120px", height: "16.8px" }}
                  >
                    <div className="placeholder rounded w-100 h-100"></div>
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center mt-1">
              <i className="bi bi-chevron-down text-secondary fs-6"></i>
            </div>
          </div>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-100 space-switcher-dropdown-menu">
        {filteredSpaces.map((space) => (
          <button
            key={space.uuid}
            onClick={() => handleSpaceSelect(space)}
            disabled={isReloading}
            className="dropdown-item btn btn-none d-flex align-items-center justify-content-between px-3 py-2 mb-1"
          >
            <div className="d-flex align-items-center w-100 gap-2">
              <div className="mb-0 text-truncate">{space.name}</div>
              {isReloading && space.uuid === tempSelectedSpace && (
                <div
                  className="ms-auto"
                  style={{ width: "20px", height: "20px" }}
                >
                  <div
                    className="spinner-border text-secondary spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </button>
        ))}
        {filteredSpaces?.length > 0 && <Dropdown.Divider />}
        <Link
          to={filteredSpaces?.length > 0 ? "/spaces" : "/spaces#create"}
          onClick={() => {
            if (filteredSpaces?.length === 0) {
              const createSpaceButton = document.querySelector(
                "#create-space-button"
              );
              if (createSpaceButton) {
                createSpaceButton.click();
              }
            }
          }}
          className="dropdown-item btn btn-none d-flex align-items-center gap-1 text-secondary"
        >
          <i className="bi bi-grid"></i>
          <span>
            {filteredSpaces?.length > 0
              ? t("spaces.viewAll")
              : t("spaces.addSpace")}
          </span>
        </Link>
      </Dropdown.Menu>
    </Dropdown>
  );
};
